import {Box, Divider, Typography} from '@mui/material';
import {t} from 'i18next';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {RootState} from '../../../../redux/store';
import {ProcessingSetting} from '../../../../types/upload-new-eviidence/evidenceSourceTypes';
import {
  INewUploadEvidence,
  setEvidenceSourceDetails,
} from '../../../../redux/slices/uploadEvidenceSourceSlice';
import AurButton from '../../../common/aur-button/AurButton';
import AurAcknowlegment from '../../../common/aur-acknowledgement/AurAcknowledgement';
import AurIcon, {IconColors, IconNames} from '../../../common/aur-icon/AurIcon';
import AurTooltip from '../../../common/aur-tooltip/AurTooltip';
import {useAnalytics} from '../../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../../constants/analyticsConstants';

interface ReviewItem {
  id: string;
  label: string;
  value?: React.ReactNode;
  toolTip?: boolean;
}

interface ReviewDetailsGridProps {
  items: ReviewItem[];
}

const ReviewReviewItem = ({items}: ReviewDetailsGridProps) => {
  return (
    <Box className="review-evidence-upload-details">
      {items.map((item, index) => (
        <Box
          key={`${index.toString() + item.label}`}
          className="review-row-data"
        >
          <Typography className="typo-text label2_size custom-text-body-subtle">
            {item.label}
          </Typography>
          {item.toolTip ? (
            <AurTooltip
              toolTipClassName="typo-text body2_size custom-header"
              className="typo-text body2_size custom-header"
              title={item.value || t('common.none')}
            >
              <Typography className="typo-text body2_size custom-header">
                {item.value || t('common.none')}
              </Typography>
            </AurTooltip>
          ) : (
            <Typography className="typo-text body2_size custom-header">
              {item.value || t('common.none')}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
};

interface ReviewEvidenceSourceProps {
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
}

const ReviewEvidenceSource: React.FC<ReviewEvidenceSourceProps> = ({
  setActiveStep,
}) => {
  const [isAcknowledged, setIsAcknowledged] = useState<boolean>(false);
  const acknowledgementDateTimeRef = useRef<string>();
  const {trackGAEvent} = useAnalytics();

  const sourceDetails = useSelector(
    (state: RootState) => state.uploadEvidenceSource?.sourceDetails
  );

  const selectedProject = useSelector(
    (state: RootState) => state.uploadEvidenceSource.selectedProject
  );

  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (sourceDetails?.acknowledgementDateTime) {
      acknowledgementDateTimeRef.current =
        sourceDetails?.acknowledgementDateTime;
      setIsAcknowledged(true);
    }
  }, [sourceDetails?.acknowledgementDateTime]);

  // Initialize reviewData whenever sourceDetails changes
  const reviewData: ReviewItem[] = useMemo(() => {
    if (!sourceDetails) return [];

    return [
      {
        id: 'evidenceName',
        label: t('reviewEvidenceSource.evidenceSourceName'),
        value: sourceDetails.evidenceName || '',
        toolTip: true,
      },
      {
        id: 'custodians',
        label: t('reviewEvidenceSource.custodians'),
        value:
          sourceDetails.custodianType === 'single'
            ? sourceDetails.singleCustodian?.displayName || ''
            : 'Multi-Custodian',
      },
      {
        id: 'intakeCompletion',
        label: t('reviewEvidenceSource.intakeCompletion'),
        value: sourceDetails.intakeCompletion?.label || '',
      },
      {
        id: 'referenceNumber',
        label: t('reviewEvidenceSource.referenceNumber'),
        value: sourceDetails.clientReference || '',
      },
      {
        id: 'includedPass',
        label: t('reviewEvidenceSource.includedPass'),
        value:
          sourceDetails.password && sourceDetails.password?.length > 0
            ? 'Yes'
            : '',
      },
    ];
  }, [sourceDetails]);

  const defaultProcessingSettings = useMemo(() => {
    if (!sourceDetails?.defaultProcessingSettings) return [];

    const columns = 3;
    const temp: ProcessingSetting[][] = Array.from({length: columns}, () => []);

    sourceDetails.defaultProcessingSettings.forEach(
      (item: ProcessingSetting, i: number) => {
        temp[i % columns].push(item);
      }
    );

    return temp.flat();
  }, [sourceDetails]);

  const essentialProcessingInstructions: ReviewItem[] = useMemo(() => {
    if (
      !sourceDetails?.uploadedManifest &&
      !sourceDetails?.uploadedPasskey &&
      !sourceDetails?.handlingInstructions
    ) {
      return [];
    }

    return [
      {
        id: 'manifest',
        label: t('reviewEvidenceSource.manifest'),
        value: sourceDetails.uploadedManifest?.fileName || '',
        toolTip: true,
      },
      {
        id: 'passkeyFile',
        label: t('reviewEvidenceSource.passkeyFile'),
        value: (sourceDetails.uploadedPasskey || [])
          .map((item) => item?.fileName)
          .filter(Boolean)
          .join(', '),
        toolTip: true,
      },
    ];
  }, [sourceDetails]);

  const handleAcknowledgeChange = useCallback(
    (isAcknowledged: boolean, acknowledgementDateTime?: string) => {
      acknowledgementDateTimeRef.current = acknowledgementDateTime;
      setIsAcknowledged(isAcknowledged);
    },
    []
  );

  const handleOnSave = useCallback(() => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.global.saveAndContinue'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.TAB,
        action: t('gaTracking.uploadEvidence.uploadEvidenceComplete'),
      },
      subPage: {
        subpage_name: t('gaTracking.uploadEvidence.review'),
        subpage_type: GA_SUBPAGE_TYPE.TAB,
      },
    });
    if (sourceDetails) {
      const updateSourceDetails: INewUploadEvidence = {
        ...sourceDetails,
        acknowledgementDateTime: acknowledgementDateTimeRef.current,
      };
      dispatch(setEvidenceSourceDetails(updateSourceDetails));
    }
    navigate('/upload-file-catalyst');
  }, [dispatch, navigate, sourceDetails]);

  return (
    <Box className="review-evidence-source">
      <Box className="review-evidence-header">
        <Typography
          className="typo-text headline3_size custom-header"
          role="heading"
          aria-level={2}
        >
          {t('reviewEvidenceSource.title')}
        </Typography>
        <AurButton
          label={t('reviewEvidenceSource.editDetails')}
          type="exit"
          size="Medium"
          title={t('reviewEvidenceSource.editDetails')}
          dataTestId="edit-button"
          onClick={() => {
            trackGAEvent({
              eventName: GA_EVENT_NAMES.CLICK,
              click: {
                name: t('gaTracking.uploadEvidence.editDetails'),
                type: GA_CLICK_TYPES.BUTTON,
                region: GA_REGIONS.TAB,
                action: t(
                  'gaTracking.uploadEvidence.uploadEvidenceEditSourceDetails'
                ),
              },
              subPage: {
                subpage_name: t('gaTracking.uploadEvidence.review'),
                subpage_type: GA_SUBPAGE_TYPE.TAB,
              },
            });

            trackGAEvent({
              eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,

              subPage: {
                subpage_name: t('gaTracking.uploadEvidence.sourceDetails'),
                subpage_type: GA_SUBPAGE_TYPE.TAB,
              },
            });
            setActiveStep((prev: any) => prev - 1);
          }}
        />
      </Box>
      <Box className="review-evidence-details">
        <Box className="review-evidence-project-details">
          <Typography
            className="typo-text subhead4_size custom-header"
            role="heading"
            aria-level={3}
          >
            {t('reviewEvidenceSource.projectTitle')}
          </Typography>
          <Typography
            data-testid="project-name"
            className="typo-text subhead1_size custom-header"
          >
            {selectedProject?.projectName}
          </Typography>
        </Box>
        <ReviewReviewItem items={reviewData} />
        <Divider orientation="horizontal" flexItem />
        <Box
          data-testid="processing-settings-section"
          className="review-evidence-processing-settings"
        >
          <Typography
            className="typo-text subhead4_size custom-header"
            role="heading"
            aria-level={3}
          >
            {t('reviewEvidenceSource.defaultProcessingSettings')}
          </Typography>
          <ReviewReviewItem items={defaultProcessingSettings} />
        </Box>
        {Object.keys(sourceDetails?.uploadedManifest || {}).length > 0 ||
        (sourceDetails?.uploadedPasskey?.length ?? 0) > 0 ||
        !!sourceDetails?.handlingInstructions?.trim() ? (
          <Box
            data-testid="essential-processing-section"
            className="review-evidence-processing-instructions"
          >
            <Divider orientation="horizontal" flexItem />
            <Box className="instructions-text-container">
              <Typography
                className="typo-text subhead4_size custom-header"
                role="heading"
                aria-level={3}
              >
                {t('reviewEvidenceSource.essentialProcessingInstructions')}
              </Typography>
              <Typography className="typo-text body3_size custom-text-instruction">
                {t('reviewEvidenceSource.manualReviewInstructions')}
              </Typography>
            </Box>
            <ReviewReviewItem items={essentialProcessingInstructions} />
            <Box className="special-instructions">
              <Typography className="typo-text label2_size custom-text-body-subtle">
                {t('reviewEvidenceSource.specialHandlingInstructions')}
              </Typography>
              <Typography
                data-testid="special-instructions"
                className="typo-text body2_size wrap-text"
              >
                {sourceDetails?.handlingInstructions || t('common.none')}
              </Typography>
            </Box>
          </Box>
        ) : null}
        <AurAcknowlegment
          labelSubText={t('reviewEvidenceSource.acknowledgementMsg')}
          showCheckbox={false}
          labelHeading={t('reviewEvidenceSource.acknowledgement')}
          initialAcknowledgementDateTime={
            sourceDetails?.acknowledgementDateTime
          }
          onAcknowledgeChange={handleAcknowledgeChange}
        />
      </Box>
      <Box className="save-action-btn">
        <AurButton
          type="continue"
          label={t('addEditTeamMember.form.saveAndContine')}
          title={t('addEditTeamMember.form.saveAndContine')}
          size="Large"
          disabled={!isAcknowledged}
          onClick={handleOnSave}
          endIcon={
            <AurIcon
              icon={IconNames.ChevronRight}
              color={IconColors.Custom}
              customClassName={'button-icon'}
            />
          }
          ariaDescription={t('eca.newExport.cancelHint')}
          dataTestId="continue-button"
        />
      </Box>
    </Box>
  );
};

export default ReviewEvidenceSource;
